import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  FieldTimeOutlined,
} from '@ant-design/icons';
import {Card, Col, DatePicker, Drawer, Row, Table, Tag, Typography} from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import {useMemo, useState} from 'react';

import {StatCard} from '../../../../CommonComponents/Cards';

const {Text, Title} = Typography;

const STATUS_CONFIG = {
  PRESENT:  {label: 'Present',  color: 'success',    icon: <CheckCircleOutlined />},
  ABSENT:   {label: 'Absent',   color: 'error',      icon: <CloseCircleOutlined />},
  LATE:     {label: 'Late',     color: 'warning',    icon: <ClockCircleOutlined />},
  HALF_DAY: {label: 'Half Day', color: 'processing', icon: <ClockCircleOutlined />},
};

/**
 * MonthlyAttendanceReport
 * Props:
 *   open         – boolean
 *   onClose      – () => void
 *   employee     – full employee object from supervisor/lookup/all
 *                  (has attendanceInfo[] array)
 */
const MonthlyAttendanceReport = ({open, onClose, employee}) => {
  const [selectedMonth, setSelectedMonth] = useState(dayjs());

  const attendanceInfo = employee?.attendanceInfo ?? [];

  // Filter records to the selected month/year
  const monthlyRecords = useMemo(() => {
    if (!selectedMonth) {return attendanceInfo;}
    return attendanceInfo.filter(r => {
      const d = dayjs(r.date);
      return d.month() === selectedMonth.month() && d.year() === selectedMonth.year();
    });
  }, [attendanceInfo, selectedMonth]);

  // Summary stats for selected month
  const stats = useMemo(() => {
    const present  = monthlyRecords.filter(r => r.status === 'PRESENT').length;
    const absent   = monthlyRecords.filter(r => r.status === 'ABSENT').length;
    const late     = monthlyRecords.filter(r => r.status === 'LATE').length;
    const halfDay  = monthlyRecords.filter(r => r.status === 'HALF_DAY').length;
    const overtime = monthlyRecords.reduce((acc, r) => acc + (r.overtimeHours ?? 0), 0);
    return {present, absent, late, halfDay, overtime, total: monthlyRecords.length};
  }, [monthlyRecords]);

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: date => (
        <Text className={'text-sm font-medium text-gray-700'}>
          {dayjs(date).format('DD MMM YYYY')}
        </Text>
      ),
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Day',
      dataIndex: 'date',
      key: 'day',
      render: date => (
        <Text className={'text-xs text-gray-400'}>{dayjs(date).format('dddd')}</Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: status => {
        const cfg = STATUS_CONFIG[status] || {label: status, color: 'default'};
        return (
          <Tag color={cfg.color} icon={cfg.icon} className={'font-medium'}>
            {cfg.label}
          </Tag>
        );
      },
    },
    {
      title: 'Overtime', dataIndex: 'overtimeType', key: 'overtimeType', width: 80,
      render: v => v ? <span className={'text-primary-600 text-sm font-medium'}>{v}</span> : '—',
    },
    {
      title: 'Login Time',
      dataIndex: 'loginTime',
      key: 'loginTime',
      render: t => (
        t ? (
          <Text className={'text-xs text-gray-500'}>
            {dayjs(t).format('hh:mm A')}
          </Text>
        ) : (
          <Text className={'text-gray-300'}>—</Text>
        )
      ),
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      render: reason =>
        reason ? (
          <Text className={'text-xs text-gray-500'}>{reason}</Text>
        ) : (
          <Text className={'text-gray-300'}>—</Text>
        ),
    },
  ];

  return (
    <Drawer
      title={(
        <div className={'flex items-center justify-between pr-6'}>
          <div className={'flex items-center gap-3'}>
            <div className={'w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center'}>
              <span className={'text-primary-600 font-bold text-sm'}>
                {employee?.firstName?.[0]}{employee?.lastName?.[0]}
              </span>
            </div>
            <div>
              <Title level={5} className={'!mb-0'}>
                {employee?.firstName} {employee?.lastName}
              </Title>
              <Text className={'text-xs text-gray-400'}>{employee?.email}</Text>
            </div>
          </div>
          <DatePicker
            picker={'month'}
            value={selectedMonth}
            onChange={val => setSelectedMonth(val)}
            allowClear={false}
            format={'MMM YYYY'}
            disabledDate={current => current && current > dayjs().endOf('month')}
          />
        </div>
      )}
      open={open}
      onClose={onClose}
      size={780}
      Styles={{padding: '16px 24px'}}
    >
      {/* Summary Stats */}
      <Row gutter={[12, 12]} className={'mb-5'}>
        <Col xs={12} sm={8}>
          <StatCard
            icon={<CheckCircleOutlined />}
            label={'Present'}
            value={stats.present}
            colorClass={'text-green-600'}
            bgClass={'bg-green-50'}
          />
        </Col>
        <Col xs={12} sm={8}>
          <StatCard
            icon={<CloseCircleOutlined />}
            label={'Absent'}
            value={stats.absent}
            colorClass={'text-red-500'}
            bgClass={'bg-red-50'}
          />
        </Col>
        <Col xs={12} sm={8}>
          <StatCard
            icon={<ClockCircleOutlined />}
            label={'Late / Half Day'}
            value={`${stats.late} / ${stats.halfDay}`}
            colorClass={'text-amber-500'}
            bgClass={'bg-amber-50'}
          />
        </Col>
        <Col xs={12} sm={8}>
          <StatCard
            icon={<FieldTimeOutlined />}
            label={'Total Overtime'}
            value={`${stats.overtime} hrs`}
            colorClass={'text-blue-500'}
            bgClass={'bg-blue-50'}
          />
        </Col>
        <Col xs={12} sm={8}>
          <StatCard
            icon={<CheckCircleOutlined />}
            label={'Days Recorded'}
            value={stats.total}
            colorClass={'text-gray-600'}
            bgClass={'bg-gray-100'}
          />
        </Col>
        <Col xs={12} sm={8}>
          <Card size={'small'} className={'rounded-xl border-0 shadow-sm bg-primary-50'}>
            <div className={'flex items-center gap-3'}>
              <div className={'p-2 bg-primary-100 rounded-lg'}>
                <span className={'text-primary-600 text-base font-bold'}>%</span>
              </div>
              <div>
                <Text className={'text-xs text-gray-400 block'}>Attendance Rate</Text>
                <Text className={'text-xl font-bold text-primary-600'}>
                  {stats.total > 0
                    ? `${Math.round(((stats.present + stats.late + stats.halfDay * 0.5) / stats.total) * 100)}%`
                    : '—'}
                </Text>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Detail Table */}
      <Card className={'rounded-xl border-0 shadow-sm'}>
        <div className={'mb-3'}>
          <Text className={'font-semibold text-gray-700'}>
            Daily Records — {selectedMonth?.format('MMMM YYYY')}
          </Text>
        </div>
        <Table
          dataSource={monthlyRecords}
          columns={columns}
          rowKey={'id'}
          size={'small'}
          pagination={{pageSize: 15, showSizeChanger: false}}
          locale={{emptyText: 'No attendance records for this month'}}
        />
      </Card>
    </Drawer>
  );
};

MonthlyAttendanceReport.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  employee: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    attendanceInfo: PropTypes.array,
  }),
};

export default MonthlyAttendanceReport;
